import { useEffect, useState } from "react";

const OVERLAY_ROOT_ID = "sgs-dev-overlay-root";
const OVERLAY_BOTTOM_GAP_PX = 16;
const OVERLAY_EXTRA_SCROLL_PX = 16;

export type HubDevOverlayInset = {
  /** Workspace pages end at the top of the chip. */
  clearancePx: number;
  /** Document pages also leave room to scroll the last control clear of the chip. */
  scrollPaddingPx: number;
};

function measureHubDevOverlayInset(): HubDevOverlayInset {
  const empty = { clearancePx: 0, scrollPaddingPx: 0 };
  const root = document.getElementById(OVERLAY_ROOT_ID);
  if (root == null) {
    return empty;
  }
  const rect = root.getBoundingClientRect();
  if (rect.height <= 0) {
    return empty;
  }
  const clearancePx = Math.ceil(window.innerHeight - rect.top);
  return {
    clearancePx,
    scrollPaddingPx: clearancePx + OVERLAY_BOTTOM_GAP_PX + OVERLAY_EXTRA_SCROLL_PX,
  };
}

/**
 * Bottom padding while the sgs-dev-launcher overlay is pinned to the viewport.
 * Workspace pages stop at the chip. Document pages keep extra room so a footer can scroll above it.
 */
export function useHubDevOverlayInset(): HubDevOverlayInset {
  const [inset, setInset] = useState<HubDevOverlayInset>({ clearancePx: 0, scrollPaddingPx: 0 });

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    let observedRoot: HTMLElement | null = null;

    const updateInset = (): void => {
      setInset(measureHubDevOverlayInset());
    };

    const observeRoot = (root: HTMLElement): void => {
      if (observedRoot === root) {
        updateInset();
        return;
      }
      resizeObserver?.disconnect();
      observedRoot = root;
      resizeObserver = new ResizeObserver(updateInset);
      resizeObserver.observe(root);
      updateInset();
    };

    const disconnect = (): void => {
      resizeObserver?.disconnect();
      resizeObserver = null;
      observedRoot = null;
    };

    const overlayRoot = document.getElementById(OVERLAY_ROOT_ID);
    if (overlayRoot instanceof HTMLElement) {
      observeRoot(overlayRoot);
    }

    const mutationObserver = new MutationObserver(() => {
      const root = document.getElementById(OVERLAY_ROOT_ID);
      if (root instanceof HTMLElement) {
        observeRoot(root);
        return;
      }
      disconnect();
      setInset({ clearancePx: 0, scrollPaddingPx: 0 });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", updateInset);

    return () => {
      mutationObserver.disconnect();
      disconnect();
      window.removeEventListener("resize", updateInset);
    };
  }, []);

  return inset;
}
